// GET /rooms/presence every 4 s, start to start, never overlapping. Unmount
// clears the timer and aborts the request in flight. A failed poll is
// reported as `failed` (the tabs show "presença indisponível", never
// "vazia") and polling continues. `refresh()` polls now (or right after the
// request in flight); each answer carries the number of the request that
// produced it, so a caller can ignore answers issued before an event.
import { PRESENCE_POLL_INTERVAL_MS } from '@kima-hub/contracts';
import type { PresenceResponse } from '@kima-hub/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isAbort, isApiError } from '../api/client.js';
import type { ApiClient } from '../api/client.js';

export type PresenceState =
  | { kind: 'loading' }
  | { kind: 'ok'; data: PresenceResponse }
  | { kind: 'failed' };

export interface PresenceOptions {
  intervalMs?: number;
  now?: () => number;
}

export interface PresenceFeed {
  state: PresenceState;
  /** Number of the request that produced `state` (0 before any answer). */
  seq: number;
  /** Number of the last request issued so far. */
  issued(): number;
  /** Poll now; if a request is in flight, poll again as soon as it settles. */
  refresh(): void;
}

export function usePresenceFeed(api: Pick<ApiClient, 'presence'>, options: PresenceOptions = {}): PresenceFeed {
  const intervalMs = options.intervalMs ?? PRESENCE_POLL_INTERVAL_MS;
  const now = options.now ?? Date.now;
  const [answer, setAnswer] = useState<{ state: PresenceState; seq: number }>({ state: { kind: 'loading' }, seq: 0 });
  const issuedRef = useRef(0);
  const refreshRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;
    let again = false;

    const poll = async () => {
      timer = null;
      again = false;
      const started = now();
      issuedRef.current += 1;
      const seq = issuedRef.current;
      controller = new AbortController();
      try {
        const data = await api.presence(controller.signal);
        if (!stopped) setAnswer({ state: { kind: 'ok', data }, seq });
      } catch (err) {
        if (stopped || isAbort(err)) return;
        // The app-level handler takes over on 401/403; polling a refused session is pointless.
        if (isApiError(err, 'unauthenticated') || isApiError(err, 'not_staff')) {
          stopped = true;
          return;
        }
        setAnswer({ state: { kind: 'failed' }, seq });
      } finally {
        controller = null;
      }
      if (stopped) return;
      const delay = again ? 0 : Math.max(0, intervalMs - (now() - started));
      timer = setTimeout(() => void poll(), delay);
    };

    refreshRef.current = () => {
      if (stopped) return;
      if (controller) {
        again = true;
        return;
      }
      if (timer !== null) clearTimeout(timer);
      void poll();
    };
    void poll();
    return () => {
      stopped = true;
      refreshRef.current = () => undefined;
      if (timer !== null) clearTimeout(timer);
      controller?.abort();
    };
  }, [api, intervalMs, now]);

  const refresh = useCallback(() => refreshRef.current(), []);
  const issued = useCallback(() => issuedRef.current, []);
  return { state: answer.state, seq: answer.seq, issued, refresh };
}

export function usePresence(api: Pick<ApiClient, 'presence'>, options: PresenceOptions = {}): PresenceState {
  return usePresenceFeed(api, options).state;
}
